import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Collapse,
  Button,
  useMediaQuery,
  useTheme,
  Pagination,
} from "@mui/material";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

const EmailHistory = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // State
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  // Filter state
  const [statusFilter, setStatusFilter] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchStats();
    fetchLogs();
  }, [statusFilter, searchFilter, page]);

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/email-logs/stats`
      );
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error("Error fetching email stats:", err);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage,
      });

      if (statusFilter) {
        params.append("status", statusFilter);
      }
      if (searchFilter) {
        params.append("search", searchFilter);
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/email-logs/?${params}`
      );

      if (!response.ok) {
        throw new Error("Nie udało się pobrać historii e-maili");
      }

      const data = await response.json();
      setLogs(data);
      
      // Calculate total pages (approximate)
      if (data.length === itemsPerPage) {
        setTotalPages(page + 1);
      } else if (page > 1) {
        setTotalPages(page);
      } else {
        setTotalPages(1);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchStats();
    fetchLogs();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "sent":
        return <CheckCircleIcon color="success" />;
      case "failed":
        return <ErrorIcon color="error" />;
      case "pending":
        return <HourglassEmptyIcon color="warning" />;
      default:
        return null;
    }
  };

  const getStatusChip = (status) => {
    const colors = {
      sent: "success",
      failed: "error",
      pending: "warning",
    };

    const labels = {
      sent: "Wysłano",
      failed: "Błąd",
      pending: "Oczekuje",
    };

    return (
      <Chip
        icon={getStatusIcon(status)}
        label={labels[status] || status}
        color={colors[status] || "default"}
        size="small"
      />
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("pl-PL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const ExpandableRow = ({ log }) => {
    const isExpanded = expandedRow === log.id;

    return (
      <>
        <TableRow hover sx={{ cursor: "pointer" }}>
          <TableCell>
            <IconButton
              size="small"
              onClick={() => setExpandedRow(isExpanded ? null : log.id)}
            >
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell>{formatDate(log.created_at)}</TableCell>
          {!isMobile && (
            <>
              <TableCell>{log.recipient_name || "-"}</TableCell>
              <TableCell>{log.recipient_email}</TableCell>
            </>
          )}
          {isMobile && (
            <TableCell>
              <Box>
                <Typography variant="body2">{log.recipient_name || log.recipient_email}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {log.recipient_name ? log.recipient_email : ""}
                </Typography>
              </Box>
            </TableCell>
          )}
          {!isTablet && <TableCell>{log.subject}</TableCell>}
          <TableCell>{getStatusChip(log.status)}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={isMobile ? 4 : isTablet ? 5 : 6}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Szczegóły
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Temat:
                    </Typography>
                    <Typography variant="body2">{log.subject}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Typ e-maila:
                    </Typography>
                    <Typography variant="body2">{log.email_type}</Typography>
                  </Grid>
                  {log.lesson_name && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Zajęcia:
                      </Typography>
                      <Typography variant="body2">
                        {log.lesson_name}
                        {log.lesson_start && (
                          <> - {formatDate(log.lesson_start)}</>
                        )}
                      </Typography>
                    </Grid>
                  )}
                  {log.sent_at && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Wysłano:
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(log.sent_at)}
                      </Typography>
                    </Grid>
                  )}
                  {log.error_message && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="error">
                        Błąd:
                      </Typography>
                      <Typography variant="body2" color="error">
                        {log.error_message}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Treść wiadomości:
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        p: 2,
                        bgcolor: "grey.100",
                        borderRadius: 1,
                        maxHeight: 300,
                        overflow: "auto",
                      }}
                      dangerouslySetInnerHTML={{ __html: log.body }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          Historia E-maili
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          variant="outlined"
          size={isMobile ? "small" : "medium"}
        >
          Odśwież
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CheckCircleIcon color="success" />
                  <Box>
                    <Typography variant="h4">{stats.total_sent}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Wysłanych
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ErrorIcon color="error" />
                  <Box>
                    <Typography variant="h4">{stats.total_failed}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Błędów
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <HourglassEmptyIcon color="warning" />
                  <Box>
                    <Typography variant="h4">{stats.total_pending}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Oczekujących
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">Wszystkie</MenuItem>
                  <MenuItem value="sent">Wysłane</MenuItem>
                  <MenuItem value="failed">Błędy</MenuItem>
                  <MenuItem value="pending">Oczekujące</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              <TextField
                fullWidth
                size="small"
                label="Szukaj (e-mail lub nazwa)"
                value={searchFilter}
                onChange={(e) => {
                  setSearchFilter(e.target.value);
                  setPage(1);
                }}
                placeholder="Wpisz e-mail lub nazwę odbiorcy..."
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <TableContainer component={Paper}>
          <Table size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Data</TableCell>
                {!isMobile && (
                  <>
                    <TableCell>Nazwa</TableCell>
                    <TableCell>E-mail</TableCell>
                  </>
                )}
                {isMobile && <TableCell>Odbiorca</TableCell>}
                {!isTablet && <TableCell>Temat</TableCell>}
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isMobile ? 4 : isTablet ? 5 : 6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isMobile ? 4 : isTablet ? 5 : 6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Brak wyników
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => <ExpandableRow key={log.id} log={log} />)
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {!loading && logs.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
              size={isMobile ? "small" : "medium"}
            />
          </Box>
        )}
      </Card>

      {/* Recent Failures */}
      {stats && stats.recent_failures && stats.recent_failures.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ErrorIcon color="error" />
              Ostatnie błędy
            </Typography>
            <Box sx={{ mt: 2 }}>
              {stats.recent_failures.map((failure) => (
                <Alert key={failure.id} severity="error" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>{failure.recipient_email}</strong> - {failure.subject}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(failure.created_at)} - {failure.error_message}
                  </Typography>
                </Alert>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default EmailHistory;
